import {
    ButtonHTMLAttributes,
    HTMLAttributes,
    KeyboardEvent,
    LiHTMLAttributes,
    MouseEvent,
    ReactNode,
    forwardRef,
} from "react";
import { cx } from "../../utils/cx";
import styles from "./ListItem.module.scss";

type CommonListItemProps = {
    /** Primary row label (Figma “Label area”). */
    children: ReactNode;
    /** Optional secondary line under the label. */
    supportingText?: ReactNode;
    /** Leading slot (radio, checkbox without label, icon, etc.). */
    leftSlot?: ReactNode;
    /** Trailing slot (value, chevron, switch, chip…). */
    rightSlot?: ReactNode;
    /** Show bottom divider. Default true. */
    divider?: boolean;
    /** Horizontal inset for the row (Figma “Gutters”). Default false. */
    gutters?: boolean;
    className?: string;
};

export type ListItemProps = CommonListItemProps &
    (
        | ({ as?: "div" } & HTMLAttributes<HTMLDivElement>)
        | ({ as: "button" } & ButtonHTMLAttributes<HTMLButtonElement>)
        | ({ as: "li" } & LiHTMLAttributes<HTMLLIElement>)
    );

/**
 * Treez DS — ListItem
 *
 * Building block for **Selection lists** in Figma (`qgFv3OQga6RQ4luvSFuGFP`,
 * `ListItem` / Selection lists). Compose with `RadioButton` (`showLabel={false}`)
 * or icon-only controls in `leftSlot`, and `SelectionList` as the list wrapper.
 */
export const ListItem = forwardRef<
    HTMLDivElement | HTMLButtonElement | HTMLLIElement,
    ListItemProps
>(function ListItem(props, ref) {
    const {
        as = "div",
        children,
        supportingText,
        leftSlot,
        rightSlot,
        divider = true,
        gutters = false,
        className,
        onClick,
        onKeyDown,
        ...rest
    } = props;

    const interactive =
        as === "button" || (as === "div" && typeof onClick === "function");

    const divKeyDown = onKeyDown as
        | HTMLAttributes<HTMLDivElement>["onKeyDown"]
        | undefined;

    const handleKeyDownDiv: (
        event: KeyboardEvent<HTMLDivElement>,
    ) => void = event => {
        divKeyDown?.(event);
        if (event.defaultPrevented) return;
        if (!onClick) return;
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            (onClick as (e: MouseEvent<HTMLDivElement>) => void)(
                event as unknown as MouseEvent<HTMLDivElement>,
            );
        }
    };

    const sharedClass = cx(styles.root, className);
    const sharedData = {
        "data-divider": divider || undefined,
        "data-gutters": gutters || undefined,
        "data-interactive": interactive || undefined,
        "data-ui": "list-item" as const,
    };

    const main = (
        <>
            <span className={styles.main}>
                {leftSlot ? (
                    <span className={styles.leftSpot}>{leftSlot}</span>
                ) : null}
                <span className={styles.body}>
                    <span className={styles.label}>{children}</span>
                    {supportingText ? (
                        <span className={styles.supporting}>
                            {supportingText}
                        </span>
                    ) : null}
                </span>
            </span>
            {rightSlot ? (
                <span className={styles.rightSpot}>{rightSlot}</span>
            ) : null}
        </>
    );

    if (as === "button") {
        const { type = "button", ...buttonRest } =
            rest as ButtonHTMLAttributes<HTMLButtonElement>;
        return (
            <button
                className={sharedClass}
                onClick={
                    onClick as ButtonHTMLAttributes<HTMLButtonElement>["onClick"]
                }
                onKeyDown={
                    onKeyDown as ButtonHTMLAttributes<HTMLButtonElement>["onKeyDown"]
                }
                ref={ref as React.ForwardedRef<HTMLButtonElement>}
                type={type}
                {...sharedData}
                {...buttonRest}
            >
                {main}
            </button>
        );
    }

    if (as === "li") {
        return (
            <li
                className={sharedClass}
                onClick={onClick as LiHTMLAttributes<HTMLLIElement>["onClick"]}
                onKeyDown={
                    onKeyDown as LiHTMLAttributes<HTMLLIElement>["onKeyDown"]
                }
                ref={ref as React.ForwardedRef<HTMLLIElement>}
                {...sharedData}
                {...(rest as LiHTMLAttributes<HTMLLIElement>)}
            >
                {main}
            </li>
        );
    }

    return (
        <div
            className={sharedClass}
            onClick={onClick as HTMLAttributes<HTMLDivElement>["onClick"]}
            onKeyDown={interactive ? handleKeyDownDiv : divKeyDown}
            ref={ref as React.ForwardedRef<HTMLDivElement>}
            role={interactive ? "button" : undefined}
            tabIndex={interactive ? 0 : undefined}
            {...sharedData}
            {...(rest as HTMLAttributes<HTMLDivElement>)}
        >
            {main}
        </div>
    );
});

ListItem.displayName = "ListItem";
